import { coachFetch } from "apps/user/learner/api/coach";

// One Configuration row for the QA test-chat picker — every Configuration,
// draft or published, is listed (a draft is exactly the case QA testing
// exists to cover before publish). Web doesn't consume @wone/sdk, so the
// learner QA client is local and self-contained.
// One agenda a Configuration can actually open a thread in. `name` is the
// blueprint's human-readable name ("Content Recommender"), `conversation_type`
// the machine key the create call takes.
export interface QaAgenda {
  conversation_type: string;
  name: string;
}

export interface QaConfigurationSummary {
  id: string;
  name: string;
  status: string;
  // False when the Configuration's content can't compose a prompt — picking
  // it yields an error instead of a reply on the first message, so the picker
  // blocks it rather than letting the tester discover that mid-conversation.
  composes: boolean;
  compose_error: string | null;
  // The agendas worth QA testing against this Configuration — it has a
  // published-or-draft version for them, and they actually read the
  // Configuration (crisis is served from the default one, so the server leaves
  // it out). A subset of what createQaThread accepts, never a superset. Empty
  // when there is nothing testable, in which case no thread should be started.
  agendas: QaAgenda[];
}

export function fetchQaConfigurations(): Promise<QaConfigurationSummary[]> {
  return coachFetch<QaConfigurationSummary[]>("/api/qa/configurations/");
}

// A newly created (or resumed) bounded QA thread. `thread_id` is what the WS
// connection targets; `conversation_id` backs the thread's transcript.
export interface QaThreadRef {
  thread_id: string;
  conversation_id: string;
  // The agenda the thread actually opened in, read back off the created
  // conversation rather than assumed from the request.
  conversation_type: string;
}

// Starts a new bounded QA thread for a Configuration without touching any
// existing thread — multiple bounded threads per Configuration coexist. A
// bounded thread always starts with its own empty memory. `conversationType`
// picks which agenda the thread opens in (it must be one the Configuration
// serves — see QaConfigurationSummary.agendas — or this 400s).
export function createQaThread(
  configId: string,
  conversationType: string
): Promise<QaThreadRef> {
  return coachFetch<QaThreadRef>("/api/qa/threads/", {
    method: "POST",
    body: {
      configuration_id: configId,
      conversation_type: conversationType,
    },
  });
}

// Archives one bounded QA thread (retained for reports, dropped from the QA
// thread list, no longer resumable).
export async function archiveQaThread(threadId: string): Promise<void> {
  await coachFetch<unknown>(`/api/qa/threads/${threadId}/archive/`, {
    method: "POST",
    body: {},
  });
}
